import { useCallback, useState } from 'react';

import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import {
	BulkAssignIssuesQuery,
	BulkOperationProgressGraphQuery,
	CreateJiraIssueQuery,
	POLL_INTERVAL_MS,
	POLL_TIMEOUT_MS,
} from './constants';
import {
	type BulkAssignIssuesResponse,
	type BulkAssignIssuesVariables,
	type BulkOperationProgressResponse,
	type BulkOperationProgressVariables,
	type CreateJiraIssueResponse,
	type CreateJiraIssueVariables,
	type FailedBulkAssignIssueEdge,
	type SuggestedEpicChildren,
} from './types';

export function useBulkAssignIssuesToEpic(): {
	loading: boolean;
	initiateBulkOperation: (
		selectedIssuesByEpic: SuggestedEpicChildren,
		cloudId: string,
	) => Promise<boolean>;
} {
	const [loading, setLoading] = useState(false);

	const initiateBulkOperation = useCallback(
		async (selectedIssuesByEpic: SuggestedEpicChildren, cloudId: string): Promise<boolean> => {
			setLoading(true);

			const assignOperations = Object.entries(selectedIssuesByEpic).map(
				async ([epicIssueKey, issueAris]) => {
					const taskId = await submitBulkAssignOperation(issueAris, epicIssueKey, cloudId);
					const poll = async (startTime: number) => {
						const isCompleted = await getBulkAssignProgress(cloudId, taskId, epicIssueKey);
						if (!isCompleted && Date.now() - startTime < POLL_TIMEOUT_MS) {
							setTimeout(() => poll(startTime), POLL_INTERVAL_MS);
						} else if (isCompleted) {
							setLoading(false);
						}
					};
					const startTime = Date.now();
					await poll(startTime);
				},
			);
			await Promise.all(assignOperations);
			setLoading(false);
			return true;
		},
		[],
	);
	return { loading, initiateBulkOperation };
}

async function submitBulkAssignOperation(
	issueAris: string[],
	epicIssueKey: string,
	cloudId: string,
): Promise<string> {
	const response = await performGqlFetch<BulkAssignIssuesResponse, BulkAssignIssuesVariables>({
		gqlQuery: BulkAssignIssuesQuery,
		variables: {
			cloudId: cloudId,
			input: {
				bulkOperationType: 'BULK_EDIT',
				bulkOperationInput: {
					bulkEditInput: {
						selectedActions: ['parent'],
						selectedIssueIds: issueAris,
						editedFieldsInput: { parentField: { issueId: epicIssueKey } },
					},
				},
			},
		},
	});
	return response.data.jira.submitBulkOperation.progress.taskId;
}

async function getBulkAssignProgress(
	cloudId: string,
	taskId: string,
	epicIssueKey: string,
): Promise<boolean> {
	const response = await performGqlFetch<
		BulkOperationProgressResponse,
		BulkOperationProgressVariables
	>({
		gqlQuery: BulkOperationProgressGraphQuery,
		variables: {
			cloudId,
			taskId,
		},
	});
	const progress = response.data.jira.bulkOperationProgress;
	if (!progress) {
		throw new Error(
			`An unexpected error occurred while bulk assigning issues to epic ${epicIssueKey}`,
		);
	} else if (progress.status === 'COMPLETE') {
		return Promise.resolve(true);
	} else if (progress.failedAccessibleIssues && progress.failedAccessibleIssues.edges.length > 0) {
		const failedIssueKeys = progress.failedAccessibleIssues.edges.map(
			(edge: FailedBulkAssignIssueEdge) => edge.node.issue.key,
		);
		const numberOfFailedIssues = failedIssueKeys.length;
		throw new Error(
			`Failed to assign ${numberOfFailedIssues} issues to epic ${epicIssueKey}: ${failedIssueKeys.join(', ')}`,
		);
	}
	return Promise.resolve(false);
}

export async function createJiraIssue({
	projectId,
	issueTypeId,
	summary,
	description,
	assignee,
}: {
	projectId: string;
	issueTypeId: string;
	summary: string;
	description: string;
	assignee?: string;
}): Promise<{ issueKey: string | null; issueUrl: string | null }> {
	const response = await performGqlFetch<CreateJiraIssueResponse, CreateJiraIssueVariables>({
		gqlQuery: CreateJiraIssueQuery,
		variables: {
			input: {
				projectId,
				issueTypeId,
				fields: {
					singleLineTextFields: [{ fieldId: 'summary', text: summary }],
					...(assignee
						? {
								singleSelectUserPickerFields: [
									{ fieldId: 'assignee', user: { accountId: assignee } },
								],
							}
						: {}),
					richTextFields: [{ fieldId: 'description', richText: { wikiText: description } }],
				},
			},
		},
	});
	return {
		issueKey: response.data.jira.createIssue.issue?.key ?? null,
		issueUrl: response.data.jira.createIssue.issue?.webUrl ?? null,
	};
}
